import { type TaskAttributes } from './generated/TaskAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import { type TaskStatusEnum_value } from './generated/types';
import Fragment from '../Fragment';
import TaskUser from './TaskUser';
import { Project, User } from '.';
import { TaskTypeEnum } from '../Enums';
import dayjs from 'dayjs';

interface Task extends TaskAttributes {}

@model
class Task extends ApplicationModel {
  // Start auto-generated
  static typename: 'Task' = 'Task';
  get typename(): 'Task' {
    return 'Task';
  }

  static internalTypename: 'Task' = 'Task';
  get internalTypename(): 'Task' {
    return 'Task';
  }

  static terminologyName = 'Task';

  static inputType = 'TaskAttributes!';

  static filterType = 'TaskFilters';
  static filters = {
    id: '[ID!]',
    personal: 'Boolean',
    myWorkTasks: 'Boolean',
    completed: 'Boolean',
    hasDueDate: 'Boolean',
    dueDate: 'ISO8601Date',
  };

  static fields = {
    assignedToUsers: attr({ writable: true, readable: false }),
    body: attr({ writable: true, readable: true }),
    commentsCount: attr({ readable: true }),
    completedByMe: attr({ readable: true }),
    completedDate: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    dueDate: attr({ writable: true, readable: true }),
    id: attr({ readable: true }),
    name: attr({ writable: true, readable: true }),
    path: attr({ readable: true }),
    position: attr({ writable: true, readable: true }),
    recurrenceRule: attr({ readable: true }),
    status: attr({ writable: true, readable: true }),
    taskUsersCount: attr({ readable: true }),
    type: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    usersCount: attr({ readable: true }),
    createdByUser: attr.belongsTo({ readable: true, typename: 'User' }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    record: attr.belongsTo({
      writeShape: ['id', 'typename'],
      readable: true,
      typename: [
        'Annotation',
        'Competitor',
        'DiscoveryInterview',
        'Epic',
        'Feature',
        'Goal',
        'Idea',
        'IdeaTheme',
        'Initiative',
        'Iteration',
        'KeyResult',
        'Page',
        'PdlcStep',
        'Persona',
        'Poll',
        'ProgramIncrement',
        'Project',
        'ProjectStrategyComponent',
        'Release',
        'ReleasePhase',
        'Requirement',
        'Task',
        'Unimplemented',
      ],
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    assigneeStatuses: attr.hasMany({ writable: true, readable: false }),
    comments: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    taskUsers: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
    users: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  constructor(attributes, options = {}) {
    super(attributes, options);
    this.local.assignedToUsers = [];
  }

  /**
   * Assign a user to the task on save:
   *
   *   task.assignUser('someone@example.com');
   *   await task.save();
   *
   * @param {string} userId User id, name or email
   */
  assignUser(userId) {
    this.local.assignedToUsers.push(userId);
  }

  isDirty() {
    return super.isDirty() || this.local.assignedToUsers.length > 0;
  }

  get isApproval() {
    return this.type === `${TaskTypeEnum.APPROVAL}`;
  }

  get isWorkRequest() {
    return this.type === `${TaskTypeEnum.WORKREQUEST}`;
  }

  get isTask() {
    return this.type === `${TaskTypeEnum.TASK}`;
  }

  get iconCssClasses() {
    return {
      [`${TaskTypeEnum.APPROVAL}`]: 'fa-regular fa-thumbs-up',
      [`${TaskTypeEnum.WORKREQUEST}`]: 'fa-regular fa-handshake',
    }[this.type];
  }

  get humanName() {
    return {
      [`${TaskTypeEnum.APPROVAL}`]: 'Approval',
      [`${TaskTypeEnum.WORKREQUEST}`]: 'Work Request',
      [`${TaskTypeEnum.TASK}`]: Task.terminologyName,
    }[this.type];
  }

  prepareAttributesForQuery(attributeKeys) {
    return {
      ...super.prepareAttributesForQuery(attributeKeys),
      assignedToUsers: this.local.assignedToUsers,
    };
  }

  resetAttributes(attributes, errors = {}) {
    super.resetAttributes(attributes, errors);
    this.local.assignedToUsers = [];
  }

  async updateStatusForAssignee(
    assigneeId: string,
    status: TaskStatusEnum_value
  ) {
    // The original query might not have loaded taskUsers, but we need them
    // to perform this mutation
    if (!this.taskUsers) {
      await this.reload({
        query: this.query.merge({
          taskUsers: TaskUser.select('id', 'status').merge({
            assignee: User.select('id').union(Project.select('id')),
          }),
        }),
      });
    }

    const taskUser = this.taskUsers.find(
      taskUser => taskUser.assignee.id === assigneeId
    );
    if (!taskUser) return;

    const fragment = new Fragment('updateTask')
      .argument('id', this.id)
      .argument(
        'attributes',
        {
          assigneeStatuses: [
            {
              id: taskUser.id,
              status: status.toLowerCase(),
            },
          ],
        },
        'TaskAttributes!'
      )
      .fragment(this.query.select('completedByMe').queryFragment);

    await this.mutate(fragment);
  }

  get taskableReferenceNum() {
    if (!this.record) return;
    if (!('referenceNum' in this.record)) return;

    return this.record.referenceNum;
  }

  dueDateGroupFrom(from = dayjs()) {
    const dueDate = this.dueDate ? dayjs(this.dueDate) : null;
    if (!dueDate) return 'No due date';
    if (dueDate.isBefore(from, 'day')) return 'Overdue';
    return dueDate.isSame(from, 'week') ? 'Due this week' : 'Due later';
  }
}

export default Task;
